import React, { useState, useEffect, useRef } from "react";
import { db, uploadFile as uploadFileToStorage } from "@/api/db";
import { Plus, Edit, Trash2, ShoppingBag, Upload, Link2, User } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const CATEGORIES = [
  { slug: "conteudo",    label: "Conteúdo & Criação" },
  { slug: "marketing",   label: "Marketing & Performance" },
  { slug: "vendas",      label: "Vendas & Comercial" },
  { slug: "dados",       label: "Dados & Operações" },
  { slug: "produto",     label: "Produto & Estratégia" },
  { slug: "comunidade",  label: "Comunidade & Atendimento" },
  { slug: "tecnologia",  label: "Tecnologia" },
];

const LEVELS = ["Iniciante", "Intermediário", "Avançado"];

const PROFESSIONS = [
  "Social Media", "Copywriter", "Designer Gráfico", "Editor de Vídeo",
  "Redator SEO", "Gestor de Tráfego", "Analista de Marketing Digital",
  "Especialista em Email Marketing", "Consultor de SEO",
  "Representante Comercial", "SDR (Sales Dev Rep)", "Account Executive",
  "Customer Success Manager", "Analista de Dados", "Especialista em BI",
  "Operador de CRM", "Gerente de E-commerce", "Product Manager",
  "UX Designer", "Estrategista de Conteúdo", "Especialista em Comunidades",
  "Gerente de Parcerias", "Analista de Suporte", "Social Listening Analyst",
  "Desenvolvedor Frontend", "Desenvolvedor Backend", "Desenvolvedor Full Stack",
  "Tech Lead", "QA Engineer", "Data Engineer", "DevOps Engineer",
];

const CATEGORY_COLORS = {
  conteudo:   { bg: "#B0FCEA", text: "#003326" },
  marketing:  { bg: "#E5FF52", text: "#003326" },
  vendas:     { bg: "#FEEDB4", text: "#65541B" },
  dados:      { bg: "#D5F9D7", text: "#008572" },
  produto:    { bg: "#EAFAF6", text: "#00664C" },
  comunidade: { bg: "#FADBDF", text: "#892E38" },
  tecnologia: { bg: "#EEE8FF", text: "#4C2B8A" },
};

const EMPTY_FORM = {
  title: "",
  description: "",
  image_url: "",
  url: "",
  original_price: "",
  price: "",
  level: "",
  category: "",
  professions: [],
  instructor_name: "",
  instructor_photo_url: "",
  is_published: true,
};

function parsePrice(str) {
  if (!str) return null;
  const n = parseFloat(str.replace(/^R\$\s*/, "").replace(",", ".").replace(/[^\d.]/g, ""));
  return isNaN(n) ? null : n;
}

function calcDiscount(original, discounted) {
  const orig = parsePrice(original);
  const disc = parsePrice(discounted);
  if (!orig || !disc || disc >= orig) return null;
  return Math.round((1 - disc / orig) * 100);
}

export default function AdminMarketplace() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingInstructor, setUploadingInstructor] = useState(false);
  const courseImgRef = useRef(null);
  const instructorImgRef = useRef(null);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    const data = await db.entities.MarketplaceCourse.list("-created_at");
    setCourses(data);
    setLoading(false);
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  function toggleProfession(prof) {
    setForm(f => {
      const list = f.professions || [];
      return {
        ...f,
        professions: list.includes(prof) ? list.filter(p => p !== prof) : [...list, prof],
      };
    });
  }

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  }

  function openEdit(course) {
    setEditing(course);
    setForm({
      title:                course.title               || "",
      description:          course.description         || "",
      image_url:            course.image_url           || "",
      url:                  course.url                 || "",
      original_price:       course.original_price      || "",
      price:                course.price               || "",
      level:                course.level               || "",
      category:             course.category            || "",
      professions:          course.professions         || [],
      instructor_name:      course.instructor_name     || "",
      instructor_photo_url: course.instructor_photo_url || "",
      is_published:         course.is_published !== false,
    });
    setShowForm(true);
  }

  async function uploadImage(file, field, setUploadingFn) {
    if (!file) return;
    setUploadingFn(true);
    try {
      const publicUrl = await uploadFileToStorage(file, "marketplace-images");
      set(field, publicUrl);
    } catch (err) {
      console.error(err);
      alert("Erro ao fazer upload: " + err.message);
    }
    setUploadingFn(false);
  }

  async function handleSave() {
    if (!form.title || !form.url) return;
    setSaving(true);
    const payload = { ...form };
    if (editing) {
      await db.entities.MarketplaceCourse.update(editing.id, payload);
    } else {
      await db.entities.MarketplaceCourse.create(payload);
    }
    await loadData();
    setShowForm(false);
    setSaving(false);
  }

  async function handleDelete(course) {
    if (!window.confirm(`Excluir "${course.title}"?`)) return;
    await db.entities.MarketplaceCourse.delete(course.id);
    await loadData();
  }

  async function togglePublished(course) {
    await db.entities.MarketplaceCourse.update(course.id, { is_published: !course.is_published });
    await loadData();
  }

  const catLabel = (slug) => CATEGORIES.find(c => c.slug === slug)?.label || slug;

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
            Marketplace de Cursos
          </h1>
          <p className="text-sm mt-0.5" style={{ color: "#99A6AB" }}>
            Gerencie os cursos externos recomendados para cada profissão.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold hover:opacity-90 transition-opacity"
          style={{ background: "#00DCA4", color: "#003326" }}
        >
          <Plus className="w-4 h-4" /> Novo Curso
        </button>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 rounded-full animate-spin"
            style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }} />
        </div>
      ) : courses.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-16 text-center">
            <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: "#00664C" }} />
            <p className="font-medium" style={{ color: "#99A6AB" }}>Nenhum curso cadastrado ainda.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {courses.map(course => {
            const cc = CATEGORY_COLORS[course.category] || { bg: "#F2F4F4", text: "#646C6F" };
            return (
              <Card key={course.id} className="border-0 shadow-sm">
                <CardContent className="p-4 flex items-center gap-4">
                  {/* Thumbnail */}
                  {course.image_url ? (
                    <img
                      src={course.image_url}
                      alt={course.title}
                      className="w-16 h-16 rounded-xl object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: "#F2F4F4" }}>
                      <ShoppingBag className="w-6 h-6" style={{ color: "#99A6AB" }} />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-sm" style={{ color: "#2E3233" }}>{course.title}</p>
                      {course.category && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{ background: cc.bg, color: cc.text }}>
                          {catLabel(course.category)}
                        </span>
                      )}
                      {course.level && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium"
                          style={{ background: "#F2F4F4", color: "#646C6F" }}>
                          {course.level}
                        </span>
                      )}
                      {course.price && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                          style={{ background: "#EAFAF6", color: "#00664C" }}>
                          {course.price}
                        </span>
                      )}
                    </div>

                    {/* Profissões */}
                    {(course.professions || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-1">
                        {course.professions.slice(0, 4).map(p => (
                          <span key={p} className="px-1.5 py-0.5 rounded text-xs"
                            style={{ background: "#F2F4F4", color: "#646C6F" }}>
                            {p}
                          </span>
                        ))}
                        {course.professions.length > 4 && (
                          <span className="text-xs" style={{ color: "#99A6AB" }}>
                            +{course.professions.length - 4}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Toggle publicado */}
                    <button
                      onClick={() => togglePublished(course)}
                      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold transition-all"
                      style={course.is_published
                        ? { background: "#B0FCEA", color: "#003326" }
                        : { background: "#F2F4F4", color: "#99A6AB" }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full"
                        style={{ background: course.is_published ? "#00664C" : "#99A6AB" }} />
                      {course.is_published ? "Publicado" : "Rascunho"}
                    </button>
                  </div>

                  {/* Ações */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => openEdit(course)}
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ color: "#99A6AB" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#F2F4F4"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <Edit className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(course)}
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ color: "#EF9A9E" }}
                      onMouseEnter={e => e.currentTarget.style.background = "#FADBDF"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Poppins, sans-serif", color: "#003326" }}>
              {editing ? "Editar Curso" : "Novo Curso"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-1">
            {/* Imagem do curso */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>
                Imagem do curso
              </label>
              <input ref={courseImgRef} type="file" accept="image/*" className="hidden"
                onChange={e => uploadImage(e.target.files[0], "image_url", setUploading)} />
              {form.image_url ? (
                <div className="relative rounded-xl overflow-hidden" style={{ aspectRatio: "16/9" }}>
                  <img src={form.image_url} alt="preview" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity"
                    style={{ background: "rgba(0,0,0,0.45)" }}>
                    <button type="button" onClick={() => courseImgRef.current?.click()}
                      className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white"
                      style={{ background: "rgba(255,255,255,0.2)" }}>
                      <Upload className="w-4 h-4" /> Trocar
                    </button>
                  </div>
                  <button type="button" onClick={() => set("image_url", "")}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                    style={{ background: "rgba(0,0,0,0.5)", color: "#fff" }}>×</button>
                </div>
              ) : (
                <button type="button" onClick={() => courseImgRef.current?.click()} disabled={uploading}
                  className="w-full flex flex-col items-center justify-center gap-2 py-8 rounded-xl transition-colors"
                  style={{ border: "2px dashed #E5E9EA", background: "#F2F4F4", color: "#99A6AB" }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#00DCA4"; e.currentTarget.style.background = "#EAFAF6"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#E5E9EA"; e.currentTarget.style.background = "#F2F4F4"; }}>
                  {uploading ? (
                    <><div className="w-5 h-5 border-2 rounded-full animate-spin"
                      style={{ borderColor: "#00DCA4", borderTopColor: "transparent" }} />
                    <span className="text-sm">Enviando...</span></>
                  ) : (
                    <><Upload className="w-5 h-5" />
                    <span className="text-sm font-medium">Clique para fazer upload</span>
                    <span className="text-xs">JPG, PNG ou WebP</span></>
                  )}
                </button>
              )}
            </div>

            {/* Título */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>Título *</label>
              <Input value={form.title} onChange={e => set("title", e.target.value)}
                placeholder="Ex: Marketing Digital do Zero ao Avançado" />
            </div>

            {/* Descrição */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>Descrição</label>
              <Textarea value={form.description} onChange={e => set("description", e.target.value)}
                placeholder="Sobre o que é esse curso..." className="resize-none" rows={2} />
            </div>

            {/* URL do curso */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>URL do curso *</label>
              <div className="relative">
                <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "#99A6AB" }} />
                <Input value={form.url} onChange={e => set("url", e.target.value)}
                  placeholder="https://..." className="pl-8" />
              </div>
            </div>

            {/* Preços */}
            {(() => {
              const discPct = calcDiscount(form.original_price, form.price);
              return (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    {/* Preço original */}
                    <div>
                      <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>
                        Preço original
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold select-none pointer-events-none"
                          style={{ color: "#646C6F" }}>R$</span>
                        <Input
                          value={form.original_price.replace(/^R\$\s*/, "")}
                          onChange={e => {
                            const stripped = e.target.value.replace(/^R\$\s*/, "");
                            set("original_price", stripped ? "R$ " + stripped : "");
                          }}
                          placeholder="197"
                          className="pl-9"
                        />
                      </div>
                    </div>

                    {/* Preço com desconto */}
                    <div>
                      <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>
                        Preço Portus
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-semibold select-none pointer-events-none"
                          style={{ color: "#646C6F" }}>R$</span>
                        <Input
                          value={form.price.replace(/^R\$\s*/, "")}
                          onChange={e => {
                            const stripped = e.target.value.replace(/^R\$\s*/, "");
                            set("price", stripped ? "R$ " + stripped : "");
                          }}
                          placeholder="97"
                          className="pl-9"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preview de desconto */}
                  {discPct && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
                      style={{ background: "#EAFAF6" }}>
                      <span className="text-xs" style={{ color: "#00664C" }}>Desconto calculado:</span>
                      <span className="px-2 py-0.5 rounded-full text-xs font-bold"
                        style={{ background: "#00DCA4", color: "#003326" }}>
                        -{discPct}%
                      </span>
                      <span className="text-xs line-through ml-auto" style={{ color: "#99A6AB" }}>
                        {form.original_price}
                      </span>
                      <span className="text-xs font-bold" style={{ color: "#003326" }}>
                        {form.price}
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Nível */}
            <div>
              <label className="text-xs font-semibold block mb-2" style={{ color: "#003326" }}>Nível</label>
              <div className="flex gap-2">
                {LEVELS.map(lvl => (
                  <button key={lvl} type="button" onClick={() => set("level", form.level === lvl ? "" : lvl)}
                    className="flex-1 py-2 rounded-full text-xs font-semibold transition-all"
                    style={form.level === lvl
                      ? { background: "#00DCA4", color: "#003326", outline: "2px solid #00DCA4", outlineOffset: "2px" }
                      : { background: "#F2F4F4", color: "#646C6F" }}>
                    {lvl}
                  </button>
                ))}
              </div>
            </div>

            {/* Categoria */}
            <div>
              <label className="text-xs font-semibold block mb-2" style={{ color: "#003326" }}>Categoria</label>
              <div className="flex gap-2 flex-wrap">
                {CATEGORIES.map(cat => {
                  const cc = CATEGORY_COLORS[cat.slug];
                  const active = form.category === cat.slug;
                  return (
                    <button key={cat.slug} type="button"
                      onClick={() => set("category", active ? "" : cat.slug)}
                      className="px-3 py-1.5 rounded-full text-xs font-semibold transition-all"
                      style={active
                        ? { background: cc.bg, color: cc.text, outline: "2px solid #00DCA4", outlineOffset: "2px" }
                        : { background: "#F2F4F4", color: "#646C6F" }}>
                      {cat.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Profissões */}
            <div>
              <label className="text-xs font-semibold block mb-2" style={{ color: "#003326" }}>
                Profissões indicadas <span className="font-normal" style={{ color: "#99A6AB" }}>(multi-seleção)</span>
              </label>
              <div className="flex gap-1.5 flex-wrap max-h-40 overflow-y-auto p-1">
                {PROFESSIONS.map(prof => {
                  const active = (form.professions || []).includes(prof);
                  return (
                    <button key={prof} type="button" onClick={() => toggleProfession(prof)}
                      className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                      style={active
                        ? { background: "#B0FCEA", color: "#003326", outline: "2px solid #00DCA4", outlineOffset: "1px" }
                        : { background: "#F2F4F4", color: "#646C6F" }}>
                      {prof}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Instrutor */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>Nome do instrutor</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: "#99A6AB" }} />
                <Input value={form.instructor_name} onChange={e => set("instructor_name", e.target.value)}
                  placeholder="Ex: João Silva" className="pl-8" />
              </div>
            </div>

            {/* Foto do instrutor */}
            <div>
              <label className="text-xs font-semibold block mb-1.5" style={{ color: "#003326" }}>Foto do instrutor</label>
              <input ref={instructorImgRef} type="file" accept="image/*" className="hidden"
                onChange={e => uploadImage(e.target.files[0], "instructor_photo_url", setUploadingInstructor)} />
              <div className="flex items-center gap-3">
                {form.instructor_photo_url ? (
                  <div className="relative">
                    <img src={form.instructor_photo_url} alt="instructor"
                      className="w-14 h-14 rounded-full object-cover" />
                    <button type="button" onClick={() => set("instructor_photo_url", "")}
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                      style={{ background: "#EF9A9E", color: "#fff" }}>×</button>
                  </div>
                ) : (
                  <div className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: "#F2F4F4" }}>
                    <User className="w-6 h-6" style={{ color: "#99A6AB" }} />
                  </div>
                )}
                <button type="button" onClick={() => instructorImgRef.current?.click()} disabled={uploadingInstructor}
                  className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all"
                  style={{ background: "#F2F4F4", color: "#646C6F" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#EAFAF6"; e.currentTarget.style.color = "#00664C"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#F2F4F4"; e.currentTarget.style.color = "#646C6F"; }}>
                  <Upload className="w-3.5 h-3.5" />
                  {uploadingInstructor ? "Enviando..." : "Upload foto"}
                </button>
              </div>
            </div>

            {/* Publicar toggle */}
            <div className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "#F2F4F4" }}>
              <button type="button" onClick={() => set("is_published", !form.is_published)}
                className="w-10 h-6 rounded-full transition-all flex-shrink-0 relative"
                style={{ background: form.is_published ? "#00DCA4" : "#D9DBDA" }}>
                <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-all"
                  style={{ left: form.is_published ? "calc(100% - 22px)" : "2px" }} />
              </button>
              <div>
                <p className="text-sm font-semibold" style={{ color: "#2E3233" }}>
                  {form.is_published ? "Publicado — visível no Marketplace" : "Rascunho — oculto"}
                </p>
              </div>
            </div>

            {/* Ações */}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => setShowForm(false)}
                className="flex-1 py-3 rounded-full text-sm font-semibold"
                style={{ background: "#F2F4F4", color: "#646C6F" }}>
                Cancelar
              </button>
              <button type="button" onClick={handleSave}
                disabled={!form.title || !form.url || saving}
                className="flex-1 py-3 rounded-full text-sm font-bold transition-opacity"
                style={form.title && form.url && !saving
                  ? { background: "#00DCA4", color: "#003326" }
                  : { background: "#E5E9EA", color: "#99A6AB" }}>
                {saving ? "Salvando..." : editing ? "Salvar alterações" : "Criar curso"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
